import {
  AlertTriangle,
  BarChart3,
  CalendarClock,
  Download,
  FileText,
  Gauge,
  LineChart,
  MapPin,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2
} from "lucide-react";
import { useMemo, useState } from "react";
import { policySources, policyVersion, regions } from "./data/policy";
import { sampleProfile, sampleSegments, scenarioPresets } from "./data/sample";
import { calculatePension } from "./engine/pension";
import type { ContributionSegment, PensionInput, ScenarioAssumption, UserProfile } from "./types";

const currency = new Intl.NumberFormat("zh-CN", {
  style: "currency",
  currency: "CNY",
  maximumFractionDigits: 0
});

const number = new Intl.NumberFormat("zh-CN", {
  maximumFractionDigits: 1
});

function regionName(code: string): string {
  return regions.find((region) => region.code === code)?.name ?? code;
}

function displayPolicyVersion(value: string): string {
  return value.replace(/\.(seed|production|prod)(\.\d+)?$/i, "");
}

function toNumber(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function inputId(): string {
  return crypto.randomUUID();
}

function createInput(
  profile: UserProfile,
  segments: ContributionSegment[],
  assumption: ScenarioAssumption
): PensionInput {
  return { profile, segments, assumption };
}

function buildReport(input: PensionInput, result: ReturnType<typeof calculatePension>): string {
  const segmentRows = input.segments
    .map(
      (segment) =>
        `| ${regionName(segment.regionCode)} | ${segment.startMonth} | ${segment.endMonth} | ${segment.paymentStatus} | ${segment.contributionBase} |`
    )
    .join("\n");

  const trace = result.formulaTrace
    .map((item) => `- ${item.label}: ${item.value}。${item.note}`)
    .join("\n");

  return `# 社保退休金测算报告

政策版本：${displayPolicyVersion(policyVersion)}
测算情景：${input.assumption.name}

## 核心结果

- 预计退休年月：${result.selectedRetirementMonth}
- 待遇领取地：${result.benefitRegionName}
- 预计月养老金：${currency.format(result.monthlyPension)}
- 基础养老金：${currency.format(result.components.basicPension)}
- 个人账户养老金：${currency.format(result.components.personalAccountPension)}
- 累计缴费：${result.actualContributionMonths} 个月
- 最低要求：${result.minimumContributionMonthsRequired} 个月

## 缴费经历

| 地区 | 开始 | 结束 | 状态 | 缴费基数 |
| --- | --- | --- | --- | --- |
${segmentRows}

## 公式追踪

${trace}

## 提示

${result.warnings.length > 0 ? result.warnings.map((warning) => `- ${warning}`).join("\n") : "- 本报告仅用于个人规划估算。"}

本报告仅用于个人规划估算，不作为社保经办机构核定结果。`;
}

function downloadText(filename: string, content: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: "text/markdown;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function App() {
  const [profile, setProfile] = useState<UserProfile>(sampleProfile);
  const [segments, setSegments] = useState<ContributionSegment[]>(sampleSegments);
  const [scenarios, setScenarios] = useState<ScenarioAssumption[]>(scenarioPresets);
  const [activeScenarioIndex, setActiveScenarioIndex] = useState(0);

  const activeScenario = scenarios[activeScenarioIndex] ?? scenarios[0];
  const activeInput = useMemo(
    () => createInput(profile, segments, activeScenario),
    [profile, segments, activeScenario]
  );
  const activeResult = useMemo(() => calculatePension(activeInput), [activeInput]);
  const comparisonResults = useMemo(
    () =>
      scenarios.map((scenario) => ({
        scenario,
        result: calculatePension(createInput(profile, segments, scenario))
      })),
    [profile, segments, scenarios]
  );

  const updateProfile = <K extends keyof UserProfile>(key: K, value: UserProfile[K]) => {
    setProfile((current) => ({ ...current, [key]: value }));
  };

  const updateScenario = <K extends keyof ScenarioAssumption>(key: K, value: ScenarioAssumption[K]) => {
    setScenarios((current) =>
      current.map((scenario, index) =>
        index === activeScenarioIndex ? { ...scenario, [key]: value } : scenario
      )
    );
  };

  const updateSegment = <K extends keyof ContributionSegment>(
    id: string,
    key: K,
    value: ContributionSegment[K]
  ) => {
    setSegments((current) =>
      current.map((segment) => (segment.id === id ? { ...segment, [key]: value } : segment))
    );
  };

  const addSegment = () => {
    setSegments((current) => [
      ...current,
      {
        id: inputId(),
        regionCode: profile.currentInsuranceRegionCode,
        startMonth: "2026-07",
        endMonth: "2026-12",
        participantType: "employee",
        paymentStatus: "paid",
        monthlySalary: 15000,
        contributionBase: 15000
      }
    ]);
  };

  const exportReport = () => {
    downloadText("pension-estimate.md", buildReport(activeInput, activeResult));
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark">
            <LineChart size={21} aria-hidden />
          </div>
          <div>
            <h1>社保退休金测算</h1>
            <p>多地缴费、断缴和延迟退休情景估算</p>
          </div>
        </div>
        <div className="topbar-actions">
          <span className="policy-chip">
            <ShieldCheck size={15} aria-hidden />
            政策版本 {displayPolicyVersion(policyVersion)}
          </span>
          <button className="button secondary" type="button" onClick={exportReport}>
            <Download size={16} aria-hidden />
            导出报告
          </button>
        </div>
      </header>

      <main className="workspace">
        <section className="panel profile-panel" aria-labelledby="profile-heading">
          <PanelTitle icon={<Gauge size={18} />} title="个人信息" action="领取资格输入" />
          <div className="form-grid one">
            <TextField
              label="出生年月"
              type="month"
              value={profile.birthMonth}
              onChange={(value) => updateProfile("birthMonth", value)}
            />
            <SelectField
              label="原法定退休类别"
              value={profile.originalRetirementAgeType}
              onChange={(value) =>
                updateProfile("originalRetirementAgeType", value as UserProfile["originalRetirementAgeType"])
              }
              options={[
                ["male60", "男职工 60 岁"],
                ["female55", "女职工 55 岁"],
                ["female50", "女职工 50 岁"]
              ]}
            />
            <SelectField
              label="户籍所在地"
              value={profile.hukouRegionCode}
              onChange={(value) => updateProfile("hukouRegionCode", value)}
              options={regions.map((region) => [region.code, region.name])}
            />
            <SelectField
              label="当前养老保险关系地"
              value={profile.currentInsuranceRegionCode}
              onChange={(value) => updateProfile("currentInsuranceRegionCode", value)}
              options={regions.map((region) => [region.code, region.name])}
            />
            <TextField
              label="个人账户余额"
              type="number"
              value={String(profile.knownAccountBalance)}
              onChange={(value) => updateProfile("knownAccountBalance", toNumber(value))}
            />
            <TextField
              label="余额截止年月"
              type="month"
              value={profile.knownAccountBalanceMonth}
              onChange={(value) => updateProfile("knownAccountBalanceMonth", value)}
            />
            <TextField
              label="视同缴费月数"
              type="number"
              value={String(profile.deemedContributionMonths)}
              onChange={(value) => updateProfile("deemedContributionMonths", toNumber(value))}
            />
          </div>

          <div className="scenario-box">
            <div className="scenario-tabs" role="tablist" aria-label="测算情景">
              {scenarios.map((scenario, index) => (
                <button
                  key={scenario.name}
                  className={index === activeScenarioIndex ? "tab active" : "tab"}
                  type="button"
                  onClick={() => setActiveScenarioIndex(index)}
                >
                  {scenario.name}
                </button>
              ))}
            </div>
            <div className="form-grid one">
              <TextField
                label="情景名称"
                value={activeScenario.name}
                onChange={(value) => updateScenario("name", value)}
              />
              <SelectField
                label="未来缴费地区"
                value={activeScenario.futureRegionCode}
                onChange={(value) => updateScenario("futureRegionCode", value)}
                options={regions.map((region) => [region.code, region.name])}
              />
              <SelectField
                label="未来缴费策略"
                value={activeScenario.contributionUntil}
                onChange={(value) =>
                  updateScenario("contributionUntil", value as ScenarioAssumption["contributionUntil"])
                }
                options={[
                  ["legalRetirement", "缴至法定退休"],
                  ["stopNow", "从现在停缴"]
                ]}
              />
              <SelectField
                label="缴费基数策略"
                value={activeScenario.baseStrategy}
                onChange={(value) => updateScenario("baseStrategy", value as ScenarioAssumption["baseStrategy"])}
                options={[
                  ["actualSalary", "按实际工资"],
                  ["p60", "按 60% 社平"],
                  ["p100", "按 100% 社平"],
                  ["p300", "按 300% 社平"]
                ]}
              />
              <TextField
                label="收入年增长"
                type="number"
                step="0.005"
                value={String(activeScenario.salaryGrowthRateAnnual)}
                onChange={(value) => updateScenario("salaryGrowthRateAnnual", toNumber(value))}
              />
              <TextField
                label="社平/计发基数年增长"
                type="number"
                step="0.005"
                value={String(activeScenario.wageGrowthRateAnnual)}
                onChange={(value) => updateScenario("wageGrowthRateAnnual", toNumber(value))}
              />
            </div>
          </div>
        </section>

        <section className="panel timeline-panel" aria-labelledby="timeline-heading">
          <div className="section-row">
            <PanelTitle icon={<CalendarClock size={18} />} title="缴费时间线" action="按工作段录入" />
            <button className="button secondary" type="button" onClick={addSegment}>
              <Plus size={16} aria-hidden />
              添加缴费段
            </button>
          </div>
          <div className="timeline-rail" aria-hidden>
            {segments.map((segment) => (
              <span
                key={segment.id}
                className={`rail-block ${segment.paymentStatus}`}
                title={`${regionName(segment.regionCode)} ${segment.startMonth} 至 ${segment.endMonth}`}
              />
            ))}
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>地区</th>
                  <th>开始</th>
                  <th>结束</th>
                  <th>身份</th>
                  <th>状态</th>
                  <th>工资</th>
                  <th>缴费基数</th>
                  <th aria-label="删除" />
                </tr>
              </thead>
              <tbody>
                {segments.map((segment) => (
                  <tr key={segment.id}>
                    <td>
                      <select
                        value={segment.regionCode}
                        onChange={(event) => updateSegment(segment.id, "regionCode", event.target.value)}
                      >
                        {regions.map((region) => (
                          <option key={region.code} value={region.code}>
                            {region.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="month"
                        value={segment.startMonth}
                        onChange={(event) => updateSegment(segment.id, "startMonth", event.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="month"
                        value={segment.endMonth}
                        onChange={(event) => updateSegment(segment.id, "endMonth", event.target.value)}
                      />
                    </td>
                    <td>
                      <select
                        value={segment.participantType}
                        onChange={(event) =>
                          updateSegment(
                            segment.id,
                            "participantType",
                            event.target.value as ContributionSegment["participantType"]
                          )
                        }
                      >
                        <option value="employee">单位职工</option>
                        <option value="flexible">灵活就业</option>
                        <option value="unemployed">未就业</option>
                        <option value="deemed">视同缴费</option>
                      </select>
                    </td>
                    <td>
                      <select
                        value={segment.paymentStatus}
                        onChange={(event) =>
                          updateSegment(
                            segment.id,
                            "paymentStatus",
                            event.target.value as ContributionSegment["paymentStatus"]
                          )
                        }
                      >
                        <option value="paid">正常缴费</option>
                        <option value="retroPaid">补缴</option>
                        <option value="interrupted">断缴</option>
                        <option value="stopped">停缴</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        value={segment.monthlySalary}
                        onChange={(event) =>
                          updateSegment(segment.id, "monthlySalary", toNumber(event.target.value))
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={segment.contributionBase}
                        onChange={(event) =>
                          updateSegment(segment.id, "contributionBase", toNumber(event.target.value))
                        }
                      />
                    </td>
                    <td>
                      <button
                        className="icon-button"
                        type="button"
                        aria-label="删除缴费段"
                        onClick={() => setSegments((current) => current.filter((item) => item.id !== segment.id))}
                      >
                        <Trash2 size={15} aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="panel result-panel" aria-labelledby="result-heading">
          <PanelTitle icon={<BarChart3 size={18} />} title="测算结果" action={activeScenario.name} />
          <div className="hero-metric">
            <span>预计月养老金</span>
            <strong>{currency.format(activeResult.monthlyPension)}</strong>
            <p>{activeResult.isEligible ? "当前情景满足领取条件" : "缴费年限暂不满足领取条件"}</p>
          </div>
          <div className="result-grid">
            <Metric label="退休年月" value={activeResult.selectedRetirementMonth} />
            <Metric label="待遇领取地" value={activeResult.benefitRegionName} />
            <Metric label="累计缴费" value={`${activeResult.actualContributionMonths} 月`} />
            <Metric label="最低要求" value={`${activeResult.minimumContributionMonthsRequired} 月`} />
            <Metric label="基础养老金" value={currency.format(activeResult.components.basicPension)} />
            <Metric label="个人账户" value={currency.format(activeResult.components.personalAccountPension)} />
          </div>
          <div className="progress-block">
            <div className="progress-label">
              <span>最低缴费完成度</span>
              <strong>
                {Math.min(
                  100,
                  (activeResult.actualContributionMonths / activeResult.minimumContributionMonthsRequired) * 100
                ).toFixed(0)}
                %
              </strong>
            </div>
            <div className="progress-track">
              <span
                style={{
                  width: `${Math.min(
                    100,
                    (activeResult.actualContributionMonths / activeResult.minimumContributionMonthsRequired) * 100
                  )}%`
                }}
              />
            </div>
          </div>
          {activeResult.warnings.length > 0 ? (
            <div className="warning-list">
              {activeResult.warnings.map((warning) => (
                <p key={warning}>
                  <AlertTriangle size={15} aria-hidden />
                  {warning}
                </p>
              ))}
            </div>
          ) : null}
        </aside>
      </main>

      <section className="lower-grid">
        <div className="panel">
          <PanelTitle icon={<RefreshCw size={18} />} title="情景比较" action="实时重算" />
          <div className="table-wrap">
            <table className="data-table compare-table">
              <thead>
                <tr>
                  <th>情景</th>
                  <th>月养老金</th>
                  <th>缴费月数</th>
                  <th>平均指数</th>
                  <th>替代率</th>
                  <th>领取地</th>
                </tr>
              </thead>
              <tbody>
                {comparisonResults.map(({ scenario, result }) => (
                  <tr key={scenario.name}>
                    <td>{scenario.name}</td>
                    <td>{currency.format(result.monthlyPension)}</td>
                    <td>{result.actualContributionMonths}</td>
                    <td>{result.averageContributionIndex.toFixed(2)}</td>
                    <td>{(result.replacementRate * 100).toFixed(1)}%</td>
                    <td>{result.benefitRegionName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="panel">
          <PanelTitle icon={<FileText size={18} />} title="公式追踪" action="可解释结果" />
          <div className="trace-list">
            {activeResult.formulaTrace.map((trace) => (
              <div className="trace-item" key={trace.label}>
                <span>{trace.label}</span>
                <strong>{trace.value}</strong>
                <p>{trace.note}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="panel">
          <PanelTitle icon={<MapPin size={18} />} title="地区累计" action="领取地判断" />
          <div className="region-stack">
            {Object.entries(activeResult.regionContributionMonths).map(([code, months]) => (
              <div className="region-row" key={code}>
                <span>{regionName(code)}</span>
                <strong>{months} 月</strong>
              </div>
            ))}
          </div>
          <div className="source-list">
            {policySources.map((source) => (
              <a key={source.id} href={source.url} target="_blank" rel="noreferrer">
                {source.title}
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function PanelTitle({ icon, title, action }: { icon: React.ReactNode; title: string; action: string }) {
  return (
    <div className="panel-title">
      <div>
        <span className="panel-icon">{icon}</span>
        <h2>{title}</h2>
      </div>
      <small>{action}</small>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  step
}: {
  label: string;
  value: string;
  type?: string;
  step?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input type={type} step={step} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: [string, string][];
  onChange: (value: string) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map(([optionValue, labelText]) => (
          <option key={optionValue} value={optionValue}>
            {labelText}
          </option>
        ))}
      </select>
    </label>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
